import React, { useState, useEffect } from 'react'
import { useScrollAnimation } from '../hooks/useScrollAnimation'
import { useTextScramble } from '../hooks/useTextScramble'

const highlights = [
	{
		icon: 'fas fa-hospital',
		text: "Software Engineer at Australia's largest clinical trials company",
	},
	{
		icon: 'fas fa-graduation-cap',
		text: 'University of Melbourne — Bachelor of Science (Computer Science)',
	},
	{
		icon: 'fas fa-gavel',
		text: 'Hackathon Mentor & Judge — HackMelbourne',
	},
	{
		icon: 'fas fa-rocket',
		text: 'Microsoft Student Accelerator Alumni',
	},
]

const StatCounter = ({ end, suffix, label, isVisible, delay = 0 }) => {
	const [count, setCount] = useState(0)

	useEffect(() => {
		if (!isVisible) return
		const timer = setTimeout(() => {
			const duration = 1800
			let startTime = null
			const animate = (timestamp) => {
				if (!startTime) startTime = timestamp
				const progress = Math.min((timestamp - startTime) / duration, 1)
				// Cubic ease-out
				const eased = 1 - Math.pow(1 - progress, 3)
				setCount(Math.floor(eased * end))
				if (progress < 1) requestAnimationFrame(animate)
			}
			requestAnimationFrame(animate)
		}, delay)
		return () => clearTimeout(timer)
	}, [isVisible, end, delay])

	return (
		<div
			className='text-center p-4 rounded-xl border border-white/8 hover:border-blue-500/30 transition-all duration-300 hover:bg-blue-500/5'
			style={{ background: 'rgba(255,255,255,0.03)' }}>
			<div className='text-2xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent'>
				{count}
				{suffix}
			</div>
			<div className='text-gray-500 text-xs mt-1 leading-tight'>{label}</div>
		</div>
	)
}

const AboutMe = () => {
	const [ref, isVisible] = useScrollAnimation(0.1)
	const scrambled = useTextScramble('ME', isVisible, 700)

	return (
		<section id='about' className='py-24 px-6 lg:px-12' ref={ref}>
			<div className='max-w-6xl mx-auto'>
				<div className='grid grid-cols-1 lg:grid-cols-2 gap-16 items-center'>
					{/* Image side */}
					<div
						className='relative flex justify-center order-2 lg:order-1 transition-all duration-700'
						style={{
							opacity: isVisible ? 1 : 0,
							transform: isVisible ? 'translateX(0)' : 'translateX(-40px)',
						}}>
						<div className='relative w-64 h-64 lg:w-80 lg:h-96'>
							<div className='absolute -inset-4 bg-gradient-to-br from-blue-500/15 to-cyan-500/15 rounded-3xl blur-xl' />
							<img
								src='/images/me.jpg'
								alt='Zakarya'
								className='relative z-10 w-full h-full object-cover rounded-2xl border border-white/10'
							/>
							<div className='absolute -bottom-4 -right-4 w-32 h-32 bg-gradient-to-br from-blue-500/20 to-cyan-500/20 rounded-2xl blur-lg pointer-events-none' />
						</div>
					</div>

					{/* Text side */}
					<div
						className='flex flex-col gap-6 order-1 lg:order-2 transition-all duration-700'
						style={{
							opacity: isVisible ? 1 : 0,
							transform: isVisible ? 'translateX(0)' : 'translateX(40px)',
							transitionDelay: '150ms',
						}}>
						<div>
							<p className='text-blue-400 font-mono text-xs tracking-[0.25em] uppercase mb-3'>
								About
							</p>
							<h2 className='text-4xl lg:text-5xl font-bold text-white mb-4'>
								About{' '}
								<span className='bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent font-mono'>
									{scrambled}
								</span>
							</h2>
							<div className='w-16 h-0.5 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full' />
						</div>

						<p className='text-gray-400 text-base leading-relaxed'>
							I'm a Full Stack Software Engineer currently building clinical
							trial technology at{' '}
							<span className='text-blue-400 font-medium'>Clinibase</span> —
							Australia's largest clinical trials company. I specialise in
							scalable web applications across the full stack, from pixel-perfect
							frontends to robust APIs and cloud infrastructure.
						</p>

						<p className='text-gray-400 text-base leading-relaxed'>
							Outside of code, I mentor the next generation of developers as a{' '}
							<span className='text-cyan-400'>
								Hackathon Judge at HackMelbourne
							</span>
							, hit the gym, and read voraciously on history, business and
							philosophy. Always up for a book recommendation!
						</p>

						{/* Highlights */}
						<div className='flex flex-col gap-2.5 mt-1'>
							{highlights.map(({ icon, text }, i) => (
								<div
									key={text}
									className='flex items-center gap-3 p-3 rounded-xl border border-white/6 transition-all duration-300 hover:border-blue-500/25 hover:translate-x-1'
									style={{
										background: 'rgba(255,255,255,0.025)',
										opacity: isVisible ? 1 : 0,
										transform: isVisible
											? 'translateX(0)'
											: 'translateX(20px)',
										transition: 'opacity 0.5s ease, transform 0.5s ease, border-color 0.3s ease',
										transitionDelay: `${300 + i * 80}ms`,
									}}>
									<div className='w-8 h-8 rounded-lg bg-blue-500/15 flex items-center justify-center flex-shrink-0'>
										<i className={`${icon} text-blue-400 text-xs`} />
									</div>
									<span className='text-gray-300 text-sm'>{text}</span>
								</div>
							))}
						</div>

						{/* Animated stat counters */}
						<div className='grid grid-cols-3 gap-3 mt-2'>
							<StatCounter
								end={7}
								suffix='+'
								label='Years Experience'
								isVisible={isVisible}
								delay={200}
							/>
							<StatCounter
								end={13}
								suffix='+'
								label='Projects Delivered'
								isVisible={isVisible}
								delay={350}
							/>
							<StatCounter
								end={2}
								suffix='K+'
								label='LinkedIn Network'
								isVisible={isVisible}
								delay={500}
							/>
						</div>
					</div>
				</div>
			</div>
		</section>
	)
}

export default AboutMe
